import { RowSelectionState } from "@tanstack/react-table";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import { uniq } from "@common/helpers/collections";
import { ScopeInsertPosition } from "../draftOperations";
import { getTypeHeaderRowId } from "./buildNestedRows";
import { NestedEditorRow } from "./nestedRowTypes";
import { NestedAddScope } from "./nestedTableMeta";

const DEFAULT_NESTED_ADD_SCOPE: NestedAddScope = {
    type: AccountType.operatingExpenses,
    parentGroupId: null,
    hostRowId: getTypeHeaderRowId(AccountType.operatingExpenses),
    hostAccountId: null,
    entityIds: [0],
    hostAccountIdByEntityId: {},
    parentGroupIdByEntityId: {},
};

// Undefined (templates) → [0]; [] (empty workspace filter) stays empty.
const typeHeaderEntityIds = (
    displayedEntityIds: number[] | undefined,
): number[] => displayedEntityIds ?? [0];

export const resolveTypeHeaderAddScope = (
    type: AccountType,
    displayedEntityIds: number[] | undefined,
): NestedAddScope => ({
    type,
    parentGroupId: null,
    hostRowId: getTypeHeaderRowId(type),
    hostAccountId: null,
    entityIds: typeHeaderEntityIds(displayedEntityIds),
    hostAccountIdByEntityId: {},
    parentGroupIdByEntityId: {},
});

export const resolveRowAddScope = (
    row: NestedEditorRow,
    displayedEntityIds: number[] | undefined,
): NestedAddScope | null => {
    if (row.kind === "group" && row.group) {
        return {
            type: row.group.type,
            parentGroupId: row.group.id,
            hostRowId: row.id,
            hostAccountId: null,
            entityIds: uniq(row.groups.map((g) => g.entityId)),
            hostAccountIdByEntityId: {},
            parentGroupIdByEntityId: Object.fromEntries(
                row.groups.map((g) => [g.entityId, g.id]),
            ),
        };
    }
    if (row.kind === "account" && row.account) {
        const { type, parentGroupId } = row.account;
        return {
            type,
            parentGroupId,
            hostRowId: row.id,
            hostAccountId: row.account.id,
            entityIds: uniq(row.accounts.map((a) => a.entityId)),
            hostAccountIdByEntityId: Object.fromEntries(
                row.accounts.map((a) => [a.entityId, a.id]),
            ),
            parentGroupIdByEntityId: Object.fromEntries(
                row.accounts.map((a) => [a.entityId, a.parentGroupId]),
            ),
        };
    }
    if (row.kind === "type" && row.accountType) {
        return {
            type: row.accountType,
            parentGroupId: null,
            hostRowId: row.id,
            hostAccountId: null,
            entityIds: typeHeaderEntityIds(displayedEntityIds),
            hostAccountIdByEntityId: {},
            parentGroupIdByEntityId: {},
        };
    }
    return null;
};

// One create per unique entityId, not per constituent (two blank-code rows in one business mint one).
export interface AddFanOutTarget {
    entityId: number;
    parentGroupId: string | null;
    position: ScopeInsertPosition;
}

export const resolveAddFanOut = (scope: NestedAddScope): AddFanOutTarget[] =>
    scope.entityIds.map((entityId) => ({
        entityId,
        parentGroupId:
            entityId in scope.parentGroupIdByEntityId
                ? scope.parentGroupIdByEntityId[entityId]
                : scope.parentGroupId,
        position:
            scope.hostAccountId != null &&
            entityId in scope.hostAccountIdByEntityId
                ? {
                      kind: "afterAccount",
                      afterAccountId: scope.hostAccountIdByEntityId[entityId],
                  }
                : { kind: "first" },
    }));

export const resolveActiveNestedAddScope = ({
    nestedRows,
    rowSelection,
    displayedEntityIds,
}: {
    nestedRows: NestedEditorRow[];
    rowSelection: RowSelectionState;
    displayedEntityIds: number[] | undefined;
}): NestedAddScope => {
    const selectedRows: NestedEditorRow[] = [];
    const visit = (row: NestedEditorRow): void => {
        if (
            rowSelection[row.id] &&
            (row.kind === "account" || row.kind === "group")
        ) {
            selectedRows.push(row);
        }
        row.children?.forEach(visit);
    };
    nestedRows.forEach(visit);

    const defaultScope: NestedAddScope = {
        ...DEFAULT_NESTED_ADD_SCOPE,
        entityIds: typeHeaderEntityIds(displayedEntityIds),
    };
    if (selectedRows.length !== 1) {
        return defaultScope;
    }
    return (
        resolveRowAddScope(selectedRows[0], displayedEntityIds) ?? defaultScope
    );
};
